import { apiEndpoints } from "@/src/config/api/endpoints";
import { api, API_VERSION } from "./base_service";
import type { ApiError, Paginated, Result } from "./types";

// ---- canonical presets (defined by the contract) --------------------------

export const ADVERSE_MEDIA_KEYWORDS = [
  "OrganizedCrime",
  "InsiderTrading",
  "RegulatoryEnforcement",
  "ESGRisk",
  "HumanRightsAbuse",
  "Corruption",
  "DrugTrafficking",
  "LawEnforcementAction",
  "CrimeGeneral",
  "TerrorismFinancing",
  "TaxEvasion",
  "Sanctions",
  "IllegalGambling",
  "EnvironmentalCrime",
  "CryptoCurrency",
  "HumanTrafficking",
  "Cybercrime",
  "SexualOffenses",
  "AntitrustViolation",
  "BankruptcyInsolvency",
  "ViolentCrime",
  "JudicialAction",
  "Fraud",
  "FinancialCrime",
] as const;
export type AdverseMediaKeyword = (typeof ADVERSE_MEDIA_KEYWORDS)[number];

export const ADVERSE_MEDIA_LANGUAGES = ["en", "es", "fr", "de", "pt"] as const;
export type AdverseMediaLanguage = (typeof ADVERSE_MEDIA_LANGUAGES)[number];

export const ADVERSE_MEDIA_KEYWORD_LABELS: Record<string, string> = {
  Terrorism: "Terrorism",
  PoliticalMisconduct: "Political misconduct",
  Crime: "Crime",
  Cybercrime: "Cybercrime",
  ViolentCrime: "Violent crime",
  PropertyCrime: "Property crime",
  FinancialCrime: "Financial crime",
  OrganizedCrime: "Organized crime",
  JudiciaryEnforcement: "Judiciary enforcement",
  BankruptcyFinancialDistress: "Bankruptcy / financial distress",
  EnvironmentalCrime: "Environmental crime",
  Sanctions: "Sanctions",
  InsiderTrading: "Insider trading",
  RegulatoryEnforcement: "Regulatory enforcement",
  ESGRisk: "ESG risk",
  HumanRightsAbuse: "Human rights abuse",
  Corruption: "Corruption",
  DrugTrafficking: "Drug trafficking",
  LawEnforcementAction: "Law enforcement action",
  CrimeGeneral: "Crime (general)",
  TerrorismFinancing: "Terrorism financing",
  TaxEvasion: "Tax evasion",
  IllegalGambling: "Illegal gambling",
  CryptoCurrency: "Cryptocurrency",
  HumanTrafficking: "Human trafficking",
  SexualOffenses: "Sexual offenses",
  AntitrustViolation: "Antitrust violation",
  BankruptcyInsolvency: "Bankruptcy / insolvency",
  JudicialAction: "Judicial action",
  Fraud: "Fraud",
};
const ADVERSE_MEDIA_LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
};
export const ADVERSE_MEDIA_KEYWORD_OPTIONS: { value: string; label: string }[] =
  ADVERSE_MEDIA_KEYWORDS.map((k) => ({ value: k, label: ADVERSE_MEDIA_KEYWORD_LABELS[k] ?? k }));
export const ADVERSE_MEDIA_LANGUAGE_OPTIONS: { value: string; label: string }[] =
  ADVERSE_MEDIA_LANGUAGES.map((l) => ({ value: l, label: ADVERSE_MEDIA_LANGUAGE_LABELS[l] ?? l }));

const ENTITY_TYPE_TO_API: Record<string, string> = {
  Individual: "Individual",
  Organization: "Entity",
  Vessel: "Vessel",
  Aircraft: "Aircraft",
  Country: "Country",
};

// ---- wire (snake) + boundary (camel) types --------------------------------

interface AdverseMediaSearchWire {
  /** `null` = strictly the search's own countries (no merge with company config). */
  country?: string[] | string | null;
  language?: string[] | string;
  keywords?: string[] | string;
  min_score?: number;
  time_horizon?: number;
}
interface SanctionsSearchWire {
  country?: string[] | string;
  data_source?: string[] | string;
  min_score?: number;
  entity_type?: string;
}
interface PepSearchWire {
  min_score?: number;
}
export interface SearchConfigurationRaw {
  id: string;
  type?: "company" | "user";
  description?: string | null;
  api_version?: string;
  adverse_media_search?: AdverseMediaSearchWire | null;
  sanctions_search?: SanctionsSearchWire | null;
  pep_search?: PepSearchWire | null;
}

export interface SearchConfigurationPayload {
  adverse_media_search?: AdverseMediaSearchWire | null;
  sanctions_search?: SanctionsSearchWire;
  pep_search?: PepSearchWire | null;
}

export interface AdverseMediaConfig {
  /** `null` = strictly defined in the search; `[]` = worldwide (merged with the search). */
  country: string[] | null;
  language: string[];
  keywords: string[];
  minScore: number | null;
  daysBack: number | null;
}
export interface SanctionsConfig {
  country: string[];
  dataSource: string[];
  minScore: number | null;
  entityType: string | null;
}
export interface PepConfig {
  minScore: number | null;
}
export interface SearchConfiguration {
  id: string;
  type: "company" | "user" | null;
  adverseMedia: AdverseMediaConfig | null;
  sanctions: SanctionsConfig | null;
  pep: PepConfig | null;
}

// ---- adapters --------------------------------------------------------------

function asArray(v: string[] | string | undefined | null): string[] {
  if (Array.isArray(v)) return v.filter((s) => typeof s === "string" && s.length > 0);
  if (typeof v === "string" && v.length > 0)
    return v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
}

export function adaptSearchConfiguration(raw: SearchConfigurationRaw): SearchConfiguration {
  const adverseMedia = raw.adverse_media_search;
  const sx = raw.sanctions_search;
  const pp = raw.pep_search;
  return {
    id: String(raw.id ?? ""),
    type: raw.type ?? null,
    adverseMedia: adverseMedia
      ? {
          country: adverseMedia.country === null ? null : asArray(adverseMedia.country),
          language: asArray(adverseMedia.language),
          keywords: asArray(adverseMedia.keywords),
          minScore: typeof adverseMedia.min_score === "number" ? adverseMedia.min_score : null,
          daysBack: timeHorizonToDaysBack(adverseMedia.time_horizon),
        }
      : null,
    sanctions: sx
      ? {
          country: asArray(sx.country),
          dataSource: asArray(sx.data_source),
          minScore: typeof sx.min_score === "number" ? sx.min_score : null,
          entityType: sx.entity_type ?? null,
        }
      : null,
    pep: pp ? { minScore: typeof pp.min_score === "number" ? pp.min_score : null } : null,
  };
}

export function hasAdverseMediaSettings(am: AdverseMediaConfig | null): boolean {
  return (
    !!am &&
    (am.keywords.length > 0 ||
      (am.country?.length ?? 0) > 0 ||
      am.language.length > 0 ||
      am.minScore != null ||
      am.daysBack != null)
  );
}

function normalizeLanguages(input: string[]): string[] {
  const allowed = new Set<string>(ADVERSE_MEDIA_LANGUAGES);
  return input.map((l) => l.toLowerCase()).filter((l) => allowed.has(l));
}

function normalizeKeywords(input: string[]): string[] {
  const canonicalByKey = new Map<string, AdverseMediaKeyword>(
    ADVERSE_MEDIA_KEYWORDS.map((k) => [k.toLowerCase(), k]),
  );
  return input
    .map((k) => canonicalByKey.get(k.toLowerCase().replace(/\s+/g, "")))
    .filter((k): k is AdverseMediaKeyword => !!k);
}

function toMinScore(confidenceScore: number): number {
  const score = confidenceScore / 100;
  if (!Number.isFinite(score)) return 0.85;
  return Math.min(1, Math.max(0.66, score));
}

export const ADVERSE_MEDIA_MAX_DAYS_BACK = 1460;

/** UI label for the `country: null` company setting. */
export const ADVERSE_MEDIA_COUNTRIES_STRICT_LABEL = "Strictly defined in the search";

function daysBackToTimeHorizon(daysBack: number): number {
  return Math.min(ADVERSE_MEDIA_MAX_DAYS_BACK, Math.max(1, Math.round(daysBack)));
}

function timeHorizonToDaysBack(value?: number): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return daysBackToTimeHorizon(value);
}

export const ADVERSE_MEDIA_TIME_HORIZON_PRESETS: { value: number | null; label: string }[] = [
  { value: null, label: "All time" },
  { value: 1, label: "1 day" },
  { value: 7, label: "1 week" },
  { value: 30, label: "1 month" },
  { value: 365, label: "1 year" },
];

export const ADVERSE_MEDIA_TIME_HORIZON_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "1 day" },
  { value: 90, label: "3 months" },
  { value: 365, label: "1 year" },
  { value: ADVERSE_MEDIA_MAX_DAYS_BACK, label: "4 years" },
];

export const ADVERSE_MEDIA_CUSTOM_UNIT_DAYS = { day: 1, month: 30, year: 365 } as const;

export interface AdverseMediaTimeHorizonParts {
  years: number;
  months: number;
  days: number;
}

export function daysBackToParts(daysBack: number): AdverseMediaTimeHorizonParts {
  const years = Math.floor(daysBack / ADVERSE_MEDIA_CUSTOM_UNIT_DAYS.year);
  const afterYears = daysBack - years * ADVERSE_MEDIA_CUSTOM_UNIT_DAYS.year;
  const months = Math.floor(afterYears / ADVERSE_MEDIA_CUSTOM_UNIT_DAYS.month);
  const days = afterYears - months * ADVERSE_MEDIA_CUSTOM_UNIT_DAYS.month;
  return { years, months, days };
}

function pluralize(amount: number, unit: string): string {
  return amount === 1 ? unit : `${unit}s`;
}

export function daysBackDescription(daysBack: number): string {
  const preset = ADVERSE_MEDIA_TIME_HORIZON_PRESETS.find((p) => p.value === daysBack);
  if (preset) return preset.label;
  const { years, months, days } = daysBackToParts(daysBack);
  const segments = [
    years > 0 ? `${years} ${pluralize(years, "year")}` : null,
    months > 0 ? `${months} ${pluralize(months, "month")}` : null,
    days > 0 ? `${days} ${pluralize(days, "day")}` : null,
  ].filter((s): s is string => s != null);
  return segments.join(", ");
}

export function daysBackLabel(daysBack: number | null): string {
  if (daysBack == null) return "All time";
  return `Last ${daysBackDescription(daysBack)}`;
}

// ---- CRUD ------------------------------------------------------------------

type SearchConfigOwnerType = "company" | "user";

function endpointsFor(type: SearchConfigOwnerType) {
  return type === "company"
    ? apiEndpoints.searchConfigurations().company()
    : apiEndpoints.searchConfigurations().user();
}

async function fetchSearchConfigurationsByType(
  type: SearchConfigOwnerType,
): Promise<Result<SearchConfiguration[]>> {
  try {
    const data = await api.get<Paginated<SearchConfigurationRaw> | SearchConfigurationRaw[]>(
      endpointsFor(type).list(),
    );
    const rows = Array.isArray(data) ? data : (data.results ?? []);
    return { ok: true, value: rows.map(adaptSearchConfiguration) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

let searchConfigurationsCache: Promise<Result<SearchConfiguration[]>> | null = null;

export function listSearchConfigurations(): Promise<Result<SearchConfiguration[]>> {
  if (!searchConfigurationsCache) {
    searchConfigurationsCache = Promise.all([
      fetchSearchConfigurationsByType("company"),
      fetchSearchConfigurationsByType("user"),
    ]).then(([companyRes, userRes]) => {
      if (!companyRes.ok) {
        searchConfigurationsCache = null;
        return companyRes;
      }
      if (!userRes.ok) {
        searchConfigurationsCache = null;
        return userRes;
      }
      return { ok: true, value: [...companyRes.value, ...userRes.value] } as Result<
        SearchConfiguration[]
      >;
    });
  }
  return searchConfigurationsCache;
}

export function invalidateSearchConfigurationsCache(): void {
  searchConfigurationsCache = null;
}

function withApiVersion(body: SearchConfigurationPayload): Record<string, unknown> {
  return { ...body, api_version: API_VERSION };
}

export async function createSearchConfiguration(
  type: SearchConfigOwnerType,
  body: SearchConfigurationPayload,
): Promise<Result<SearchConfiguration>> {
  try {
    const data = await api.post<SearchConfigurationRaw>(
      endpointsFor(type).create(),
      withApiVersion(body),
    );
    invalidateSearchConfigurationsCache();
    return { ok: true, value: adaptSearchConfiguration(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function updateSearchConfiguration(
  type: SearchConfigOwnerType,
  id: string,
  body: SearchConfigurationPayload,
): Promise<Result<SearchConfiguration>> {
  try {
    const data = await api.patch<SearchConfigurationRaw>(
      endpointsFor(type).update(id),
      withApiVersion(body),
    );
    invalidateSearchConfigurationsCache();
    return { ok: true, value: adaptSearchConfiguration(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function deleteSearchConfiguration(
  type: SearchConfigOwnerType,
  id: string,
): Promise<Result<void>> {
  try {
    await api.delete<void>(endpointsFor(type).delete(id));
    invalidateSearchConfigurationsCache();
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

function isAlreadyExists(error: ApiError): boolean {
  if (error.status < 400) return false;
  const haystack = [error.message, ...Object.values(error.fieldErrors ?? {}).flat()]
    .join(" ")
    .toLowerCase();
  return haystack.includes("already exist") || haystack.includes("use patch");
}

function existingIdFromError(error: ApiError): string | undefined {
  const haystack = [error.message, ...Object.values(error.fieldErrors ?? {}).flat()].join(" ");
  const match = haystack.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  return match?.[0];
}

export async function upsertSearchConfiguration(
  type: SearchConfigOwnerType,
  body: SearchConfigurationPayload,
): Promise<Result<SearchConfiguration>> {
  const created = await createSearchConfiguration(type, body);
  if (created.ok || !isAlreadyExists(created.error)) return created;

  const idFromError = existingIdFromError(created.error);
  if (idFromError) return updateSearchConfiguration(type, idFromError, body);

  const list = await listSearchConfigurations();
  if (!list.ok) return { ok: false, error: list.error };
  const existing = list.value.find((c) => c.type === type);
  if (!existing) return created;
  return updateSearchConfiguration(type, existing.id, body);
}

// ---- typed savers (the page consumers call these) -------------------------

export interface AdverseMediaInput {
  /** `null` = strictly defined in the search (sent explicitly so PATCH clears it). */
  countries: string[] | null;
  languages: string[];
  keywords: string[];
  confidenceScore: number;
  daysBack: number | null;
}

export async function saveCompanyAdverseMedia(
  input: AdverseMediaInput,
  existingId?: string,
): Promise<Result<SearchConfiguration>> {
  const body: SearchConfigurationPayload = {
    adverse_media_search: {
      country: input.countries,
      language: normalizeLanguages(input.languages),
      keywords: normalizeKeywords(input.keywords),
      min_score: toMinScore(input.confidenceScore),
      ...(input.daysBack != null ? { time_horizon: daysBackToTimeHorizon(input.daysBack) } : {}),
    },
  };
  return existingId
    ? updateSearchConfiguration("company", existingId, body)
    : upsertSearchConfiguration("company", body);
}

export interface SanctionsDefaultInput {
  countries: string[];
  dataSources: string[];
  confidenceScore: number;
  entityType: string | null;
}

export async function saveUserSanctionsDefault(
  input: SanctionsDefaultInput,
  existingId?: string,
): Promise<Result<SearchConfiguration>> {
  const sanctions_search: SanctionsSearchWire = {
    country: input.countries,
    data_source: input.dataSources,
    min_score: toMinScore(input.confidenceScore),
  };
  if (input.entityType) {
    sanctions_search.entity_type = ENTITY_TYPE_TO_API[input.entityType] ?? input.entityType;
  }
  const body: SearchConfigurationPayload = { sanctions_search };

  if (existingId) {
    return updateSearchConfiguration("user", existingId, body);
  }
  return upsertSearchConfiguration("user", body);
}

// ---- PEP match sensitivity (company-wide) ----------------------------------

export interface PepSensitivityInput {
  confidenceScore: number;
}

export async function savePepSensitivity(
  input: PepSensitivityInput,
  existingId?: string,
): Promise<Result<SearchConfiguration>> {
  const body: SearchConfigurationPayload = {
    pep_search: { min_score: toMinScore(input.confidenceScore) },
  };
  return existingId
    ? updateSearchConfiguration("company", existingId, body)
    : upsertSearchConfiguration("company", body);
}

export async function clearPepSensitivity(
  existingId: string,
): Promise<Result<SearchConfiguration>> {
  return updateSearchConfiguration("company", existingId, { pep_search: null });
}

export async function clearCompanyAdverseMedia(
  existingId: string,
): Promise<Result<SearchConfiguration>> {
  return updateSearchConfiguration("company", existingId, { adverse_media_search: null });
}
